import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Topheader from "../../component/Topheader";
import { Eye, Users, MessageSquare, Calendar } from "lucide-react";
import { apiClient } from "../../config";

const LeadDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const mapContactToLead = (c) => ({
      _id: c._id,
      fullName: c.name || "-",
      email: c.email || "-",
      status: c.status || "New",
      priority: c.priority || "Medium",
      createdAt: c.createdAt || new Date().toISOString(),
    });

    const mapUniversityToInquiry = (u) => ({
      _id: u._id,
      institutionName: u.university_name || "-",
      contactPersonName: u.contact_person_name || "-",
      status: u.status || "New",
      priority: u.priority || "Medium",
      createdAt: u.createdAt || new Date().toISOString(),
    });

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError("");

        const [leadsResponse, inquiriesResponse] = await Promise.all([
          apiClient.get("/api/contact"),
          apiClient.get("/api/auth/universities"),
        ]);

        const leadPayload = leadsResponse.data;
        const inquiryPayload = inquiriesResponse.data;

        const contacts = Array.isArray(leadPayload?.contacts)
          ? leadPayload.contacts
          : [];
        const universities = Array.isArray(inquiryPayload?.universities)
          ? inquiryPayload.universities
          : [];

        setLeads(contacts.map(mapContactToLead));
        setInquiries(universities.map(mapUniversityToInquiry));
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to load dashboard data");
        setLeads([]);
        setInquiries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const leadSummary = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    return {
      totalLeads: leads.length,
      todayLeads: leads.filter(
        (lead) => new Date(lead.createdAt) >= startOfToday,
      ).length,
    };
  }, [leads]);

  const inquirySummary = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    return {
      totalInquiries: inquiries.length,
      todayInquiries: inquiries.filter(
        (inquiry) => new Date(inquiry.createdAt) >= startOfToday,
      ).length,
    };
  }, [inquiries]);

  const recentLeads = useMemo(() => {
    return [...leads]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 7);
  }, [leads]);

  const recentInquiries = useMemo(() => {
    return [...inquiries]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
  }, [inquiries]);

  const SummaryCard = ({ title, value, icon: Icon, color, bg }) => (
    <div className="card border shadow-sm">
      <div className="card-body p-4 d-flex justify-content-between align-items-center">
        <div>
          <h6
            className="text-muted text-uppercase fw-semibold mb-2"
            style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}
          >
            {title}
          </h6>
          <p className="fw-bold mb-0 text-dark fs-4">{value}</p>
        </div>
        <div
          style={{
            background: bg,
            borderRadius: "12px",
            padding: "0.6rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={20} color={color} />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-4">
        <h1 className="h4 fw-bold mb-1">Lead Manager Dashboard</h1>
        <p className="text-muted">
          Welcome back, here's your lead and inquiry summary.
        </p>
        {loading && (
          <p className="text-muted mb-0 mt-2">
            Loading dashboard data...
          </p>
        )}
        {!loading && error && (
          <p className="text-danger mb-0 mt-2">{error}</p>
        )}
      </div>

      {/* Summary Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <SummaryCard title="Total Leads" value={leadSummary.totalLeads} icon={Users} color="#435ebe" bg="#eef2ff" />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="Total Inquiries"
            value={inquirySummary.totalInquiries}
            icon={MessageSquare}
            color="#10b981"
            bg="#ecfdf5"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="Today's Leads"
            value={leadSummary.todayLeads}
            icon={Calendar}
            color="#f59e0b"
            bg="#fffbeb"
          />
        </div>
        <div className="col-md-3">
          <SummaryCard
            title="Today's Inquiries"
            value={inquirySummary.todayInquiries}
            icon={Calendar}
            color="#ef4444"
            bg="#fef2f2"
          />
        </div>
      </div>

      {/* Recent Leads Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header border-bottom d-flex align-items-center justify-content-between bg-transparent">
          <h5 className="mb-0 fw-semibold text-dark">Recent Leads</h5>
          <Link to="/leads" className="btn btn-sm btn-outline-secondary">
            View All Leads
          </Link>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Status</th>
                  <th scope="col">Priority</th>
                  <th scope="col">Date</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.length > 0 ? (
                  recentLeads.map((lead, index) => (
                    <tr key={lead._id}>
                      <th scope="row">{index + 1}</th>
                      <td>{lead.fullName}</td>
                      <td>{lead.email}</td>
                      <td>
                        <span
                          className={`badge bg-${lead.status === "New" ? "success" : lead.status === "Qualified" ? "primary" : "warning"}`}
                        >
                          {lead.status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge bg-${lead.priority === "High" ? "danger" : lead.priority === "Medium" ? "info" : "secondary"}`}
                        >
                          {lead.priority}
                        </span>
                      </td>
                      <td>
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <Link
                          to={`/lead/${lead._id}`}
                          className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
                        >
                          <Eye size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Inquiries Table */}
      <div className="card border-0 shadow-sm mt-4">
        <div className="card-header border-bottom d-flex align-items-center justify-content-between bg-transparent">
          <h5 className="mb-0 fw-semibold text-dark">Recent Inquiries</h5>
          <Link to="/inquiries" className="btn btn-sm btn-outline-secondary">
            View All Inquiries
          </Link>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Institution</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Status</th>
                  <th scope="col">Priority</th>
                  <th scope="col">Date</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentInquiries.length > 0 ? (
                  recentInquiries.map((inquiry, index) => (
                    <tr key={inquiry._id}>
                      <th scope="row">{index + 1}</th>
                      <td>{inquiry.institutionName}</td>
                      <td>{inquiry.contactPersonName}</td>
                      <td>
                        <span
                          className={`badge bg-${inquiry.status === "New" ? "success" : inquiry.status === "Qualified" ? "primary" : "warning"}`}
                        >
                          {inquiry.status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge bg-${inquiry.priority === "High" ? "danger" : inquiry.priority === "Medium" ? "info" : "secondary"}`}
                        >
                          {inquiry.priority}
                        </span>
                      </td>
                      <td>
                        {new Date(inquiry.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <Link
                          to={`/inquiry/${inquiry._id}`}
                          className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center"
                        >
                          <Eye size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No inquiries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDashboard;
